import {
  camelCase,
  cloneDeep,
  defaults,
  get,
  isEqual,
  startCase,
  times,
  upperFirst,
  zipObject,
} from "lodash-es";
import {
  addressSearchProviderType,
  BookStatusEntry,
  GDT,
  IBaseMap,
  PbAddressResponse,
  User,
} from "@/types";
import moment from "moment";
import SA from "sweetalert2";
import { useGoogleBaseMaps } from "@/config";
import mapboxgl from "mapbox-gl";
import defaultMapStyle from "@/assets/data/OS_OpenZoomstack.json";
import { Feature, FeatureCollection } from "geojson";
import { DatasetRecord } from "@/types/DatasetRecord";
import { FindAddressResponse, GetECADDataResponse } from "@/types/autoaddress";
import { AnyLocation } from "@/types/location";
import pinia, {
  DataBoundary,
  useApi,
  useDataSource,
  useGammaData,
  useMap,
  useOrgSettings,
} from "@/services";
import { buffer, envelope, length as tLength, point } from "@turf/turf";
import Papa from "papaparse";

export const humanize = (value: string): string =>
  startCase(value?.split("@")[0]).replace("Oneill", "O’Neill");

export const initials = (user: User): string =>
  humanize(user.email)
    .split(" ")
    .map((w) => w[0])
    .join("");

export const timeAgo = (value: string) =>
  value ? moment(value, "YYYY-MM-DD HH:mm:ss").fromNow() : value;

export const date2filename = (date?: Date) => {
  return moment(date).format("YYYY-MM-DD_H-m-s");
};
export const formatDate = (date?: string) => {
  return moment(date).format("DD/MM/YYYY");
};

export const Confirmation = SA.mixin({
  title: "Please confirm",
  buttonsStyling: false,
  customClass: {
    confirmButton: "btn btn-info",
    cancelButton: "btn btn-outline-danger mr-3",
  },
  showCloseButton: true,
  showCancelButton: true,
  reverseButtons: true,
  focusConfirm: true,
  focusCancel: false,
});

export const emptyGeoJSON = {
  type: "FeatureCollection",
  features: [],
} as GeoJSON.FeatureCollection<GeoJSON.MultiLineString, any>;

export const castValueType = (value: any, type: string) =>
  ["Integer", "Decimal"].includes(type)
    ? +value
    : type === "Boolean"
    ? ["1", "true", 1, true].includes(value)
    : ["String", "Bytes", "DateTime", "Jsonb"].includes(type)
    ? "" + value
    : value;

const makeRasterStyle = (tiles: string[]): mapboxgl.Style => ({
  version: 8,
  sources: {
    "raster-tiles": {
      type: "raster",
      tiles,
      tileSize: 256,
    },
    empty: {
      type: "geojson",
      data: emptyGeoJSON,
    },
  },
  layers: [
    {
      id: "base-raster",
      type: "raster",
      source: "raster-tiles",
      minzoom: 0,
      maxzoom: 22,
    },
    {
      id: "above-base",
      type: "line",
      source: "empty",
    },
  ],
  glyphs:
    "https://perilfinder4-static.gamma.ie/tiles/OSBG/Zoomstack/OS-open-assets/fonts/{fontstack}/{range}.pbf",
});

export const resolveBaseMapStyle = (
  b: IBaseMap
): Promise<mapboxgl.Style | string> =>
  new Promise((resolve) => {
    if (b.style) {
      resolve(b.style);
    } else {
      const googleBaseMaps = useGoogleBaseMaps(useOrgSettings().googleMapsKey);
      const currentLayer =
        googleBaseMaps.find((m: IBaseMap) => m.name === b.name) ||
        googleBaseMaps[0];

      switch (b.type) {
        case "Bing":
          resolve(
            makeRasterStyle([
              b.mapTypeId === "roadmap"
                ? `https://ecn.t2.tiles.virtualearth.net/tiles/r{quadkey}.jpeg?g=7897&key=${
                    b.licence || b.licenceKey
                  }&shading=hill`
                : `https://ecn.t2.tiles.virtualearth.net/tiles/a{quadkey}.jpeg?g=7830&key=${
                    b.licence || b.licenceKey
                  }`,
            ])
          );
          break;
        case "Google":
          resolve(
            makeRasterStyle([
              `${currentLayer.url}&key=${currentLayer.licenceKey}`,
            ])
          );
          break;
        case "OSi":
          resolve(makeRasterStyle([`${b.url}${b.licence}`]));
          break;
        default:
          resolve(defaultMapStyle as mapboxgl.Style);
      }
    }
  });

const parseDigitsInfo = (info: string): number[] =>
  ([] as string[])
    .concat(...info.split(".").map((i: string) => i.split("-")))
    .map((i: string) => Number(i));

/**
 * inspired by Angular's formatNumber fn,
 * (used in DecimalPipe, CurrencyPipe & PercentPipe)
 * which, in turn, is a handy wrapper for Intl.NumberFormat
 *
 * @param value number
 * @param options Partial<NumberFormatOptions> & { digitsInfo }
 */
export const formatValue = function (
  value?: number,
  options?: {
    style?: string;
    currency?: string;
    digitsInfo?: string; // Same as https://angular.io/api/common/DecimalPipe#parameters
  }
) {
  options = defaults(options || {}, {
    style: "currency",
    currency: "EUR",
    digitsInfo: "1.0-0",
  });
  if (typeof value !== "number") {
    return "";
  }
  const [minimumIntegerDigits, minimumFractionDigits, maximumFractionDigits] =
    parseDigitsInfo(options.digitsInfo as string);
  const { style, currency } = options;
  return new Intl.NumberFormat("en-IE", {
    style,
    currency,
    minimumIntegerDigits,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
};

export const getCurrencySign = function (currencySymbol: string): string {
  const currencySymbols = [
    {
      label: "€",
      value: "EUR",
    },
    {
      label: "£",
      value: "GBP",
    },
  ];
  return (
    currencySymbols.find((item) => item.label === currencySymbol)?.value ||
    "EUR"
  );
};

export const createGeoJSONSquare = function (
  center: GeoJSON.Position,
  km: number
) {
  const squareCenter = point(center);
  const buf = buffer(squareCenter, km / 2, { units: "kilometers" });
  const square = envelope(buf);
  return {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: [square],
    } as GeoJSON.FeatureCollection,
  };
};

export const createGeoJSONCircle = function (
  center: GeoJSON.Position,
  radiusInKm: number,
  points = 64
) {
  const coords = {
    latitude: center[1],
    longitude: center[0],
  };

  const km = radiusInKm;

  const ret: number[][] = [];
  const distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
  const distanceY = km / 110.574;

  let theta, x, y;
  for (let i = 0; i < points; i++) {
    theta = (i / points) * (2 * Math.PI);
    x = distanceX * Math.cos(theta);
    y = distanceY * Math.sin(theta);

    ret.push([coords.longitude + x, coords.latitude + y]);
  }
  ret.push(ret[0]);

  return {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [ret],
          },
        },
      ],
    } as GeoJSON.FeatureCollection,
  };
};

export const getGeoJSONCircleCenter = (points: any[]) =>
  points
    .reduce((acc, el) => [acc[0] + el[0], acc[1] + el[1]], [0, 0])
    .map((n: number) => n / points.length);

export const wrapDrawFeature = (feature: GeoJSON.Feature<any>) => {
  const clone = cloneDeep(feature);
  if (feature.properties?.circleRadius) {
    clone.geometry.coordinates[0] = times(5, () =>
      getGeoJSONCircleCenter(feature.geometry.coordinates[0])
    );
  }
  return clone;
};

export const unwrapDrawFeature = (feature: any) =>
  validateBoundary({
    ...feature,
    geometry: get(feature, "properties.circleRadius")
      ? createGeoJSONCircle(
          getGeoJSONCircleCenter(feature.geometry.coordinates[0]),
          feature.properties.circleRadius
        ).data.features[0].geometry
      : feature.geometry,
  });

const validateBoundary = (feature: any): DataBoundary => {
  const coordinates = get(feature, "geometry.coordinates[0]") || [];
  if (
    coordinates.reduce((acc: number[][], el: number[]) => {
      if (!acc.some((e) => isEqual(el, e))) {
        acc.push(el);
      }
      return acc;
    }, []).length > 2
  ) {
    return feature;
  }
  return null;
};

export const routeMapper = (obj: Record<string, any>) =>
  zipObject(
    Object.keys(obj),
    Object.values(obj).map((val) =>
      typeof val === "string" ? val : JSON.stringify(val)
    )
  );

export const titleCase = (text: string | undefined) =>
  text
    ? text
        .split("_")
        .reduce(
          (x, y) =>
            `${x} ${y.charAt(0).toUpperCase()}${y.slice(1).toLowerCase()}`,
          ""
        )
        .trim()
    : undefined;
export const pascalCase = (s: string) => upperFirst(camelCase(s));
export const patchUrl = (url: string) =>
  url.replace("{base}", useApi(pinia).baseDataUrl);

const ragIcons = {
  accident: require("../assets/icons/accident.svg"),
  burglary: require("../assets/icons/burglary.svg"),
  crime: require("../assets/icons/crime.svg"),
  fire: require("../assets/icons/fire.svg"),
  flood: require("../assets/icons/flood.svg"),
  jba: require("../assets/icons/flood.svg"),
  floodclimatechange: require("../assets/icons/flood.svg"),
  other: require("../assets/icons/other.svg"),
  storm: require("../assets/icons/storm.svg"),
  subsidence: require("../assets/icons/subsidence.svg"),
} as Record<string, any>;

export const getRiskIcon = (term: string): string =>
  ragIcons[
    Object.keys(ragIcons).find((k) => term.toLowerCase().indexOf(k) > -1) ||
      "other"
  ];

export function getRagIconUrl(name: string): string {
  const key =
    Object.keys(ragIcons).find((k) => name.toLowerCase().indexOf(k) > -1) ||
    "other";
  return ragIcons[key];
}

export const makeGeometryFilter = (
  field: string,
  geojson: GeoJSON.Polygon | GeoJSON.MultiPolygon
): GDT.DataFilter => ({
  functionName: "FieldIntersectsGeometryFromGeoJson",
  arguments: [
    {
      name: "field",
      value: field,
    },
    {
      name: "geojson",
      value: geojson,
    },
    {
      name: "srid",
      value: "4326",
    },
  ],
  conditionOperator: 0,
});

export const parseAccumulationItem = (
  row: unknown[],
  columns: GDT.ColumnDefinition[],
  props: Record<string, any>
) => new DatasetRecord(row, columns, props);

export const calculateTotalSumInsured = (entries: DatasetRecord[]): number =>
  entries
    .map((e: any) => e.totalSumInsured || 0)
    .reduce((a: number, b: number) => a + b, 0);

export const formatSumReducer = (entries: any[], prop: string): string =>
  formatValue(
    entries
      .map((e: any) => get(e, prop) || 0)
      .reduce((a: number, b: number) => a + b, 0),
    {
      digitsInfo: "1.2-2",
      currency: getCurrencySign(useOrgSettings().currencySymbol),
    }
  );

export const displayCoordinates = (coords: number[] = []) =>
  JSON.stringify(coords.map((s) => Math.round(s * 1e5) / 1e5).reverse());

export const filterReset = {
  fieldName: "",
  filterOperator: 0,
  value: null as string | number | null,
  conditionOperator: 0,
};

export const autoAddressToPrecisely = (
  {
    vanityAddress,
    geographicAddress,
    postalAddress,
    postcode,
  }: FindAddressResponse,
  ecadData: GetECADDataResponse
): PbAddressResponse => {
  const addressArr = vanityAddress || geographicAddress || postalAddress;
  const addressStr = addressArr.join(", ");
  return {
    address: {
      formattedAddress: addressStr,
      mainAddressLine: "",
      addressLastLine: "",
      placeName: "",
      areaName1: "",
      areaName2: "",
      areaName3: "",
      areaName4: "",
      postCode1: postcode || "N/A",
      postCodeExt: "",
      country: "IRL",
      addressNumber: "",
      streetName: "",
      unitType: "",
      unitValue: "",
    },
    geometry: {
      type: "Point",
      coordinates: [
        ecadData.spatialInfo.etrs89.location.longitude,
        ecadData.spatialInfo.etrs89.location.latitude,
      ],
    },
    ranges: [],
    totalUnitCount: 0,
    formattedLocationAddress: postcode || "",
    formattedStreetAddress:
      (get(geographicAddress, "english") as string) || addressStr,
    precisionLevel: 16,
  };
};

export const addressJoiner = (args: any[]): string =>
  args.filter((o) => o && typeof o === "string").join(", ");

export const formatAddress = (address: any): string =>
  address
    ? address.formattedLocationAddress && address.formattedStreetAddress
      ? addressJoiner([
          address.formattedStreetAddress,
          address.formattedLocationAddress,
        ])
      : address.addressLastLine
      ? address.addressLastLine
      : addressJoiner([address.areaName3, address.areaName2, address.areaName1])
    : "";

export const roundCoordinate = (n: any) =>
  (typeof n === "number" && Math.round(n * 1e7) / 1e7) || n;

export const cropCanvas = (
  source: HTMLCanvasElement,
  left: number,
  top: number,
  width: number,
  height: number
) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.drawImage(source, left, top, width, height, 0, 0, width, height);
  }
  return canvas;
};

export type lengthUnit = "m" | "km" | "mi";

export const lengthUnits = new Map<lengthUnit, number>([
  ["m", 1],
  ["km", 0.001],
  ["mi", 0.000621371],
]);

export const metricConvertor = (unit: lengthUnit): number =>
  lengthUnits.get(unit) || 1;
export const distanceRounder = (value: number, unit: lengthUnit): number =>
  unit === "m" ? Math.round(value) : Math.round(value * 1e3) / 1e3;

export const roundDecimal = (amount: number, decimals = 3): number =>
  Math.round(amount * Math.pow(10, decimals - 1)) / Math.pow(10, decimals - 1);

export const getLocationDataRequestV2 = (
  location: AnyLocation,
  useEcadId = true
) => {
  if (location.type === "Geometry") {
    return location.addressLinkRequest;
  }
  if (
    location.address?.addressProvider === "Precisely" &&
    location.address.precisionLevel === 16
  ) {
    return {
      Address: location.address,
    };
  }

  if (location.type === "Address" && useEcadId) {
    return location.addressLinkRequest;
  } else {
    return {
      Coordinates: location.geometry.coordinates,
    };
  }
};

export const riskRequestFromAccuracy = (location: AnyLocation) => {
  const { precisionAccuracy } = location;
  if (typeof precisionAccuracy === "number") {
    switch (true) {
      case precisionAccuracy === 110:
        return {
          Coordinates: location.geometry.coordinates,
        };
      case precisionAccuracy < 200:
        return location.geometry.type === "Point"
          ? { Coordinates: location.geometry.coordinates }
          : { Geometry: location.geometry };
      case precisionAccuracy < 300:
        return location.addressLinkRequest;
      default:
        return null;
    }
  }
  return null;
};

export const getRisksRequestV2 = (
  location: AnyLocation,
  coordinatesOnly = false
) => {
  if (location.type === "Address" && coordinatesOnly) {
    return {
      Coordinates: location.geometry.coordinates,
    };
  }

  const request = riskRequestFromAccuracy(location);
  if (request) return request;

  if (location.type === "Geometry") {
    return location.addressLinkRequest;
  }
  if (
    location.address?.addressProvider === "Precisely" &&
    location.address?.precisionLevel === 16
  ) {
    return {
      Coordinates: location.geometry.coordinates,
    };
  }

  if (location.type === "Address") {
    return location.addressLinkRequest;
  } else {
    return {
      Coordinates: location.geometry.coordinates,
    };
  }
};

export const defaultAddressSearchProvider = {
  providerType: addressSearchProviderType.PitneyBowes,
  api: {
    base: "https://api.precisely.com/",
    autocomplete: "typeahead/v1/locations",
    geocode: "geocode/v1/premium/geocode",
    reverseGeocode: "geocode/v1/premium/reverseGeocode",
    gamma: "https://build.gamma.ie/Gamma.WS/api/PitneyBowesAuth/GetToken",
  },
  licenceKey: "MqW11-k08u2-hjd6F",
  country: "GBR",
  maxCandidates: 10,
  distance: 500,
};

export function exportToCsv(filename: string, rows: any[]) {
  if (!rows || !rows.length) {
    return;
  }
  const csvContent = Papa.unparse(rows);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename + ".csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export async function updateGammaDataSourceData(
  args: GDT.DataSourceOptions
): Promise<void> {
  if (!args.mapBoundsPolygon) {
    const s = args.map.getSource(args.name) as mapboxgl.GeoJSONSource;
    s.setData(emptyGeoJSON);
    return;
  }
  let shouldLoad = args.shouldLoad;
  let i = 0;
  while (!shouldLoad && i < args.layers.length) {
    const layer = args.map.getLayer(args.layers[i]);
    if (layer) {
      shouldLoad =
        args.map.getLayoutProperty(layer.id, "visibility") === "visible" &&
        args.map.getZoom() >= (get(layer, "minzoom") || 0) &&
        args.map.getZoom() <= (get(layer, "maxzoom") || 23);
    }
    i += 1;
  }

  if (!shouldLoad) {
    return;
  }

  // checks the layers visibility if any of them is visible.
  // the visiblity property is set somewhere else (map component) :-(.
  const isVisible = args.layers.some((id) => {
    const layer = args.map.getLayer(id);
    return (
      args.map.getLayoutProperty(layer.id, "visibility") === "visible" &&
      args.map.getZoom() >= (get(layer, ["minzoom"]) || 0) &&
      args.map.getZoom() <= (get(layer, ["maxzoom"]) || 23)
    );
  });

  if (!isVisible) {
    return;
  }

  const geometryFilter =
    args.source.geometryField && args.mapBoundsPolygon
      ? makeGeometryFilter(args.source.geometryField, args.mapBoundsPolygon)
      : null;

  const dataSource = useDataSource(args.name)();
  const request: GDT.DataRequest = {
    catalogUri: args.source.dataRequest?.catalogUri as string,
    request: {
      ...args.source.dataRequest,
      filter: geometryFilter
        ? dataSource?.allFilters("spatial").concat(geometryFilter)
        : dataSource?.allFilters("spatial"),
    },
  };

  if (dataSource) {
    const r: {
      data: string | GeoJSON.Feature | GeoJSON.FeatureCollection;
    } = await useGammaData().fetchGeoJson(request, "map");

    dataSource.latestGeoJsonRequest = request;
    const s = args.map.getSource(args.name) as mapboxgl.GeoJSONSource;
    if (s) {
      s.setData(r?.data || emptyGeoJSON);
    }
  }
}

export function addGammaDataSource(args: GDT.DataSourceOptions) {
  args.map.addSource(args.name, {
    type: "geojson",
    generateId: true,
    data: emptyGeoJSON,
    cluster: args.source.cluster ? args.source.cluster : false,
    clusterRadius: args.source.clusterRadius ? args.source.clusterRadius : 0,
    clusterProperties: args.source.clusterProperties
      ? args.source.clusterProperties
      : {},
  } as mapboxgl.AnySourceData);

  if (useMap().dataBoundary?.geometry) {
    updateGammaDataSourceData({
      ...args,
      mapBoundsPolygon: useMap().dataBoundary?.geometry,
    });
  }
}

export function getBookLayerName(book: BookStatusEntry): string {
  return `${book.name} (${book.id.slice(-6)})`;
}

export function calcLength(
  geojson: Feature<any, any> | FeatureCollection<any, any>
): string {
  const length = tLength(geojson);
  return length < 1
    ? Math.round((length * 100000) / 100) + "m"
    : Math.round((length * 100) / 100) + "km";
}

export function meterSquaredToAcres(val: number): number {
  return val / 4047;
}
